import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const KEY = "sb_lang";
const LangContext = createContext(null);

export const LANGUAGES = [
  { id: "en", label: "English", flag: "🇬🇧" },
  { id: "mr", label: "मराठी", flag: "🇮🇳" },
  { id: "hi", label: "हिन्दी", flag: "🇮🇳" },
];

const DICT = {
  en: {
    "brand.tagline": "Community Discovery",
    "nav.cities": "Cities", "nav.categories": "Categories", "nav.vendors": "Vendors", "nav.apps": "Apps",
    "header.login": "Login", "header.invite": "Request Invite", "header.cart": "Cart",
    "header.notifications": "Notifications", "header.messages": "Messages",
    "menu.profile": "Profile", "menu.vendor_dashboard": "Vendor Dashboard",
    "menu.become_vendor": "Become a Vendor", "menu.admin": "Admin Console", "menu.logout": "Logout",
    "hero.badge": "Closed Community Platform",
    "hero.title_1": "Discover trusted", "hero.title_brand": "Brahmin-owned", "hero.title_2": "businesses — Maharashtra & beyond.",
    "hero.subtitle": "sanskariBazarIn brings vendors, services and our community together. No carts, no payments — just direct discovery and conversation between consumers and verified local businesses.",
    "hero.invite": "I have an invite code", "hero.member_login": "Member Login", "hero.explore": "Explore Cities",
    "hero.search_placeholder": "Search business, service, product, vendor, city…",
    "stats.vendors": "Vendors", "stats.members": "Members", "stats.cities": "Cities", "stats.categories": "Categories",
    "section.browse_city_eyebrow": "Browse by city",
    "section.browse_city_title": "Maharashtra at the core. India & abroad too.",
    "section.see_all_cities": "See all {n} cities →",
    "section.nearest": "Nearest detected:",
    "section.bazaar_eyebrow": "Indian Bazaar",
    "section.bazaar_title_1": "Today in the", "section.bazaar_title_brand": "bazaar",
    "section.bazaar_subtitle": "Trending categories, fresh listings and items members are searching right now. One tap to discover, chat and connect.",
    "section.bazaar_popular": "Popular items right now",
    "whyus.referral_title": "Referral-only access",
    "whyus.referral_text": "Every member is invited and verified. The community curates itself.",
    "whyus.talk_title": "Talk directly",
    "whyus.talk_text": "Built-in chat connects you to vendors. Decide prices, delivery and service together.",
    "whyus.roots_title": "Cultural roots, modern tools",
    "whyus.roots_text": "Three handcrafted themes, 28 categories and Marathi-style design — built for our people.",
    "common.search": "Search", "common.chat": "Chat with vendor", "common.whatsapp": "WhatsApp",
    "common.add_to_cart": "Add to enquiry cart", "common.send_inquiry": "Send inquiry",
    "common.featured": "Featured", "common.open_today": "Open today", "common.explore": "Explore →",
    "common.back_home": "← Back to home",
    "cart.empty": "Your cart is empty.", "cart.title": "Enquiry Cart",
    "login.title": "Member Login",
    "login.intro": "Sign in with Google. First-time? You will need a referral code from an existing member.",
    "login.referral_label": "Referral code (optional if returning member)",
    "login.cta": "Continue with Google",
    "login.notice": "Every membership is reviewed by admins. Fake or non-eligible referrals will be blocked along with the referrer and all related accounts.",
    "register.title": "Request your invite",
    "register.subtitle": "sanskariBazarIn is closed and members-only. Enter the referral code from your inviter.",
    "register.referral_label": "Referral code",
    "register.checking": "Checking…",
    "register.referred_by": "Referred by",
    "register.invalid": "Code not recognised",
    "register.accept": "I agree to not use abusive language, sell illegal substances, or post any adult images or pornographic material on this platform. I also accept the Terms and Disclaimer below.",
    "register.policy_title": "Community Agreement (mandatory)",
    "register.cta": "Continue with Google",
    "register.review_notice": "New accounts are reviewed by an admin before full access is granted.",
    "footer.platform": "Platform", "footer.legal": "Legal",
    "footer.all_vendors": "All Vendors", "footer.contact": "Contact",
    "footer.terms": "Terms & Conditions", "footer.privacy": "Privacy Policy",
    "footer.disclaimer": "Disclaimer",
    "footer.about": "A closed, members-only platform to discover, connect, and chat with Brahmin-owned businesses across Maharashtra and beyond.",
    "footer.rights": "All rights reserved.",
    "footer.brand_subtitle": "Community Business Discovery",

    /* ---------- Top ribbon ---------- */
    "ribbon.welcome": "Welcome to sanskariBazarIn — invite-only community marketplace",
    "ribbon.cart": "Cart",

    /* ---------- VendorListing filter bar ---------- */
    "vl.eyebrow": "Browse vendors",
    "vl.loading": "Loading…",
    "vl.results_count": "{n} results — searches business, products & services",
    "vl.title_default": "All Vendors",
    "vl.title_city": "Vendors in {city}",
    "vl.title_category_city": "{category} in {city}",
    "vl.label_search": "Search",
    "vl.search_placeholder": "Search vendor, item, city, category — e.g. 'Modak', '2 BHK', 'Paithani saree'",
    "vl.label_city": "City",
    "vl.all_cities": "All cities",
    "vl.label_category": "Category",
    "vl.all_categories": "All categories",
    "vl.label_sort": "Sort",
    "vl.sort_recent": "Recently added",
    "vl.sort_rating": "Rating",
    "vl.verified": "Verified",
    "vl.open_now": "Open now",
    "vl.min_rating": "Min rating",
    "vl.rating_any": "Any",
    "vl.view_grid": "Grid",
    "vl.view_map": "Map",
    "vl.per_page": "Per page",
    "vl.matched_items": "Matched items",
    "vl.btn_view": "View",
    "vl.btn_whatsapp": "WhatsApp",
    "vl.btn_chat": "Chat",
    "vl.pagination": "Page {page} of {total}",
    "vl.previous": "Previous",
    "vl.next": "Next",

    /* ---------- BecomeVendor wizard ---------- */
    "bv.eyebrow": "Become a vendor",
    "bv.title": "List your business",
    "bv.subtitle": "Fill in your business details. Admin approval is required before your profile becomes visible to other members.",
    "bv.business_name": "Business name *",
    "bv.category": "Category *",
    "bv.choose_category": "Choose category",
    "bv.city": "City *",
    "bv.choose_city": "Choose city",
    "bv.description": "Description",
    "bv.description_placeholder": "Brief about your business",
    "bv.phone": "Phone",
    "bv.whatsapp": "WhatsApp",
    "bv.address": "Address",
    "bv.website": "Website",
    "bv.hours": "Working hours",
    "bv.service_area": "Service area",
    "bv.service_area_placeholder": "e.g. Pune & nearby",
    "bv.logo_url": "Logo URL",
    "bv.banner_url": "Banner URL",
    "bv.location_title": "Pinpoint on map (optional)",
    "bv.location_text": "Tap below while standing at your shop. We'll drop a precise pin instead of using the {city} centroid.",
    "bv.recapture": "Re-capture",
    "bv.use_my_location": "Use my current location",
    "bv.location_captured": "Location captured",
    "bv.geo_unsupported": "Geolocation not supported on this device",
    "bv.geo_denied": "Location permission denied",
    "bv.geo_failed": "Could not get location",
    "bv.disclaimer": "Disclaimer",
    "bv.cancel": "Cancel",
    "bv.submit": "Submit for approval",
    "bv.saving": "Saving…",
    "bv.created_toast": "Vendor profile created. Awaiting admin approval.",
    "bv.create_failed": "Could not create vendor",

    /* ---------- CartPage ---------- */
    "cp.eyebrow": "Enquiry cart",
    "cp.items": "{n} items",
    "cp.item_singular": "{n} item",
    "cp.subtitle": "Group inquiries by vendor. Each vendor receives your full list as one chat message — terms, price & delivery are agreed mutually.",
    "cp.clear_all": "Clear all",
    "cp.empty": "Your enquiry cart is empty.",
    "cp.browse_vendors": "Browse vendors",
    "cp.items_count": "{n} items",
    "cp.send_inquiry": "Send inquiry & chat",
    "cp.sent": "Inquiry sent to vendor",
    "cp.send_failed": "Could not send",

    /* ---------- NotificationCenter ---------- */
    "nc.eyebrow": "Updates from the community",
    "nc.title": "Notifications",
    "nc.empty": "No notifications yet.",
    "nc.from": "From",
    "nc.type_offer": "offer",
    "nc.type_new_service": "new service",
    "nc.type_update": "update",
    "nc.type_event": "event",

    /* ---------- VendorProfile ---------- */
    "vp.loading": "Loading…",
    "vp.verified": "Verified",
    "vp.open_now": "Open now",
    "vp.closed": "Closed",
    "vp.by": "By",
    "vp.chat_with_vendor": "Chat with vendor",
    "vp.chat_on_whatsapp": "Chat on WhatsApp",
    "vp.report": "Report",
    "vp.report_dialog_title": "Report this vendor",
    "vp.report_placeholder": "Describe the issue…",
    "vp.report_submit": "Submit",
    "vp.report_cancel": "Cancel",
    "vp.report_success": "Report submitted to admin",
    "vp.offer": "Offer:",
    "vp.tab_products": "Products / Services",
    "vp.tab_gallery": "Gallery",
    "vp.tab_reviews": "Reviews",
    "vp.tab_contact": "Contact",
    "vp.tab_about": "About Us",
    "vp.tab_map": "Map",
    "vp.service_area": "Service area:",
    "vp.working_hours": "Working hours:",
    "vp.no_products": "No products listed yet.",
    "vp.videos": "Videos",
    "vp.photos": "Photos",
    "vp.no_gallery": "No gallery items yet.",
    "vp.map_fallback": "Showing the {city} centroid. Ask the vendor to share an exact address for a precise pin.",
    "vp.disclaimer": "Disclaimer",
    "vp.disclaimer_text": "Transactions, pricing, delivery, taxation and service conditions are directly between the consumer and the vendor. sanskariBazarIn does not facilitate payments or orders.",
    "vp.add_to_cart_short": "Add to enquiry cart",
    "vp.added_to_cart": "{name} added to enquiry cart",

    /* ---------- ChatPage ---------- */
    "chat.conversations": "Conversations",
    "chat.tab_active": "Active",
    "chat.tab_archived": "Archived",
    "chat.empty_active": "No conversations yet.",
    "chat.empty_archived": "No archived chats.",
    "chat.archive": "Archive",
    "chat.restore": "Restore",
    "chat.archived_toast": "Chat archived",
    "chat.restored_toast": "Chat restored",
    "chat.update_failed": "Could not update chat",
    "chat.select_to_start": "Select a conversation to start chatting",
    "chat.browse_vendors": "Or browse vendors →",
    "chat.subtitle": "Direct chat — transactions outside the platform",
    "chat.view_profile": "View profile",
    "chat.say_hello": "Say hello…",
    "chat.be_first": "Be the first to say hello.",
    "chat.image_ready": "Image ready to send",
    "chat.placeholder": "Type a message — or paste an image…",
    "chat.image_only": "Only image files",
    "chat.upload_failed": "Upload failed",
    "chat.vendor_fallback": "Vendor",
    "chat.read": "read",
    "chat.sent": "sent",

    /* ---------- VendorDashboard ---------- */
    "vd.title_eyebrow": "Vendor dashboard",
    "vd.approved": "Approved",
    "vd.awaiting_approval": "Awaiting admin approval",
    "vd.verified": "Verified",
    "vd.unverified": "Unverified",
    "vd.open_now_toggle": "Open now",
    "vd.tab_info": "Profile",
    "vd.tab_orders": "Orders / Inquiries",
    "vd.tab_notify": "Notifications",
    "vd.business_info": "Business info",
    "vd.business_name": "Business name",
    "vd.category": "Category",
    "vd.city": "City",
    "vd.phone": "Phone",
    "vd.whatsapp": "WhatsApp",
    "vd.website": "Website",
    "vd.address": "Address",
    "vd.description": "Description",
    "vd.working_hours": "Working hours",
    "vd.service_area": "Service area",
    "vd.map_pin": "Map pin",
    "vd.exact_pin": "Exact pin:",
    "vd.using_city_centroid": "Currently using {city} centroid. Capture your shop's exact location for a precise pin.",
    "vd.recapture": "Re-capture",
    "vd.use_my_location": "Use my current location",
    "vd.location_captured": "Location captured — remember to Save",
    "vd.location_cleared": "Location cleared",
    "vd.logo": "Logo",
    "vd.banner": "Banner",
    "vd.offer_announcement": "Offer / announcement",
    "vd.offer_placeholder": "Festive 10% off…",
    "vd.gallery": "Gallery",
    "vd.gallery_count": "{n} / 25 images",
    "vd.embedded_videos": "Embedded videos (YouTube · Instagram · Facebook)",
    "vd.videos_count": "{n} / 5 videos",
    "vd.video_url_placeholder": "https://www.youtube.com/watch?v=… or https://www.instagram.com/p/…",
    "vd.add_video_link": "+ Add a video link",
    "vd.subscription_note": "Base subscription allows up to 25 images and 5 embedded videos in the gallery.",
    "vd.save": "Save",
    "vd.profile_saved": "Profile saved",
    "vd.save_failed": "Could not save",
    "vd.orders_heading": "Orders & Inquiries",
    "vd.orders_refresh": "Refresh",
    "vd.orders_note": "Settlement & payment happen offline between you and the customer. Use chat to confirm details.",
    "vd.orders_empty": "No orders yet. Consumer cart inquiries land in chat first.",
    "vd.loading": "Loading…",
    "vd.order_marked": "Order marked {status}",
    "vd.order_update_failed": "Could not update order",
    "vd.status_new": "New",
    "vd.status_in_progress": "In progress",
    "vd.status_delivered": "Delivered",
    "vd.status_cancelled": "Cancelled",
    "vd.notify_heading": "Send a notification",
    "vd.notify_subtitle": "Reach everyone in the community. (SMS / WhatsApp broadcast placeholder — coming soon.)",
    "vd.notify_title_placeholder": "Title (e.g. Diwali special)",
    "vd.notify_type_offer": "New Offer",
    "vd.notify_type_service": "New Service",
    "vd.notify_type_update": "Business Update",
    "vd.notify_type_event": "Event Announcement",
    "vd.notify_message": "Message",
    "vd.notify_send": "Send to community",
    "vd.notify_sent": "Notification sent",
    "vd.notify_blocked": "Vendor must be approved to send notifications",
    "vd.no_profile_title": "No vendor profile yet",
    "vd.no_profile_text": "Become a vendor to start receiving inquiries.",
    "vd.no_profile_cta": "Become a vendor",
    "vd.geo_unsupported": "Geolocation not supported",
    "vd.geo_denied": "Permission denied",
    "vd.geo_failed": "Could not get location",
    "vd.clear_failed": "Could not clear",
    "vd.products_label": "Products / services in your inventory",
    "vd.products_count": "{n} items",
    "vd.products_hint": "Add the items, dishes, services or properties you offer — each can have its own picture, description and indicative price. Consumers see these on your profile.",
    "vd.product_add": "+ Add an item",
    "vd.product_remove": "Remove",
    "vd.product_name_placeholder": "Item name (e.g. Modak, 2 BHK Apartment, Wedding photoshoot)",
    "vd.product_price_placeholder": "Indicative price (e.g. ₹120/box, ₹65 Lakh)",
    "vd.product_desc_placeholder": "Short description / specs",

    /* ---------- Apps mega-menu ---------- */
    "apps.menu_label": "Apps",
    "apps.menu_subtitle": "Specialised verticals on the same trusted vendor network",
    "apps.gharonda.name": "Gharonda",
    "apps.gharonda.tagline": "Find your home — sale, rent, plots & builders",
    "apps.gharonda.cta": "Browse properties",
    "apps.khavayye.name": "Khavayye",
    "apps.khavayye.tagline": "Home-cooked meals, tiffins, caterers, restaurants",
    "apps.khavayye.cta": "Find food vendors",
    "apps.soulmates.name": "Soul Mates",
    "apps.soulmates.tagline": "Matchmakers & wedding services within the community",
    "apps.soulmates.cta": "Find a matchmaker",
    "apps.sabkuchfresh.name": "Sab Kuch Fresh",
    "apps.sabkuchfresh.tagline": "Fresh fruits, vegetables, organic grocers — direct from local sellers",
    "apps.sabkuchfresh.cta": "Order groceries",
    "apps.niramaya.name": "Niramaya",
    "apps.niramaya.tagline": "Ayurveda, allopathy, homeopathy & more — care from your community",
    "apps.niramaya.cta": "Find a practitioner",
    "apps.rozgari.name": "Rozgari",
    "apps.rozgari.tagline": "Jivika — jobs shared by our members, for our members",
    "apps.rozgari.cta": "Browse openings",
    "apps.coming_soon": "Coming soon",
    "apps.view_all": "View all {name} listings",
    "apps.no_vendors": "No verified vendors here yet. Invite one from your community →",
    "apps.filter_listing_type": "Listing type",
    "apps.filter_bhk": "BHK",
    "apps.filter_cuisine": "Cuisine / type",
    "apps.filter_service": "Service type",
    "apps.filter_delivery": "Delivery",
    "apps.filter_any": "Any",

    /* ---------- AdminDashboard ---------- */
    "ad.eyebrow": "Admin console",
    "ad.title": "Control room",
    "ad.subtitle": "Manage members, vendors, referrals and reports.",
    "ad.stat_total_users": "Total users",
    "ad.stat_approved_vendors": "Approved vendors",
    "ad.stat_pending_vendors": "Pending vendors",
    "ad.stat_pending_users": "Pending users",
    "ad.stat_blocked": "Blocked",
    "ad.stat_reports": "Reports",
    "ad.tab_users": "Users",
    "ad.tab_vendors": "Vendor approvals",
    "ad.tab_analytics": "Analytics",
    "ad.tab_referrals": "Referral tree",
    "ad.tab_reports": "Reports",
    "ad.tab_ads": "Ads",
    "ad.tab_maintenance": "Maintenance",
    "ad.filter_all": "all",
    "ad.filter_pending": "pending",
    "ad.filter_approved": "approved",
    "ad.filter_blocked": "blocked",
    "ad.search_placeholder": "Search…",
    "ad.search": "Search",
    "ad.col_name": "Name",
    "ad.col_email": "Email",
    "ad.col_role": "Role",
    "ad.col_status": "Status",
    "ad.col_referrer": "Referrer code",
    "ad.col_actions": "Actions",
    "ad.col_business": "Business",
    "ad.col_owner": "Owner",
    "ad.col_category": "Category",
    "ad.col_city": "City",
    "ad.col_vendor": "Vendor",
    "ad.col_reason": "Reason",
    "ad.col_when": "When",
    "ad.col_rating": "Rating",
    "ad.btn_approve": "Approve",
    "ad.btn_block": "Block",
    "ad.btn_block_cascade": "Block + cascade",
    "ad.btn_unblock": "Unblock",
    "ad.btn_hide": "Hide",
    "ad.user_approved": "User approved",
    "ad.user_blocked": "User blocked",
    "ad.user_unblocked": "Unblocked",
    "ad.cascade_blocked": "User and downstream blocked",
    "ad.vendor_approved": "Vendor approved",
    "ad.vendor_hidden": "Vendor hidden",
    "ad.analytics_by_city": "Vendors by city",
    "ad.analytics_by_category": "Vendors by category",
    "ad.analytics_top": "Top rated vendors",
    "ad.referrals_heading": "Referral tree",
    "ad.referrals_hint": "Use \"Block + cascade\" on the Users tab to block a referrer and all downstream referrals.",
    "ad.reports_empty": "No reports.",
    "ad.tag_approved": "approved",
    "ad.tag_pending": "pending",
    "ad.tag_blocked": "blocked",
  },
  mr: {
    "brand.tagline": "समुदाय शोध",
    "nav.cities": "शहरे", "nav.categories": "श्रेण्या", "nav.vendors": "विक्रेते", "nav.apps": "ॲप्स",
    "header.login": "लॉगिन", "header.invite": "आमंत्रण मागवा", "header.cart": "कार्ट",
    "header.notifications": "सूचना", "header.messages": "संदेश",
    "menu.profile": "प्रोफाइल", "menu.vendor_dashboard": "विक्रेता डॅशबोर्ड",
    "menu.become_vendor": "विक्रेता व्हा", "menu.admin": "ॲडमिन कन्सोल", "menu.logout": "लॉगआउट",
    "hero.badge": "बंद समुदाय व्यासपीठ",
    "hero.title_1": "विश्वासू", "hero.title_brand": "ब्राह्मण-मालकीचे", "hero.title_2": "व्यवसाय शोधा — महाराष्ट्र आणि पुढे.",
    "hero.subtitle": "sanskariBazarIn विक्रेते, सेवा आणि आपला समुदाय एकत्र आणते. कार्ट नाही, पेमेंट नाही — फक्त थेट शोध आणि ग्राहक व पडताळलेल्या स्थानिक व्यवसायांमध्ये संवाद.",
    "hero.invite": "माझ्याकडे आमंत्रण आहे", "hero.member_login": "सदस्य लॉगिन", "hero.explore": "शहरे पाहा",
    "hero.search_placeholder": "व्यवसाय, सेवा, वस्तू, विक्रेता, शहर शोधा…",
    "stats.vendors": "विक्रेते", "stats.members": "सदस्य", "stats.cities": "शहरे", "stats.categories": "श्रेण्या",
    "section.browse_city_eyebrow": "शहरानुसार पाहा",
    "section.browse_city_title": "महाराष्ट्र केंद्रस्थानी. भारत व विदेशही.",
    "section.see_all_cities": "सर्व {n} शहरे पाहा →",
    "section.nearest": "जवळचे शहर:",
    "section.bazaar_eyebrow": "इंडियन बाजार",
    "section.bazaar_title_1": "आजचा", "section.bazaar_title_brand": "बाजार",
    "section.bazaar_subtitle": "ट्रेंडिंग श्रेण्या, ताज्या नोंदी आणि सदस्य आत्ता काय शोधत आहेत. एका क्लिकवर शोधा, संवाद साधा आणि जोडले जा.",
    "section.bazaar_popular": "सध्या लोकप्रिय वस्तू",
    "whyus.referral_title": "केवळ आमंत्रणाने प्रवेश",
    "whyus.referral_text": "प्रत्येक सदस्य आमंत्रित आणि पडताळलेला असतो. समुदाय स्वतःच नियंत्रित करतो.",
    "whyus.talk_title": "थेट बोला",
    "whyus.talk_text": "अंगभूत चॅट तुम्हाला विक्रेत्यांशी जोडतो. किंमत, वितरण आणि सेवा एकत्र ठरवा.",
    "whyus.roots_title": "सांस्कृतिक मूळ, आधुनिक साधने",
    "whyus.roots_text": "तीन हस्तनिर्मित थीम, २८ श्रेण्या आणि मराठी शैलीतील डिझाइन — आमच्या लोकांसाठी.",
    "common.search": "शोधा", "common.chat": "विक्रेत्याशी संवाद", "common.whatsapp": "व्हॉट्सॲप",
    "common.add_to_cart": "चौकशी कार्टमध्ये जोडा", "common.send_inquiry": "चौकशी पाठवा",
    "common.featured": "वैशिष्ट्यपूर्ण", "common.open_today": "आज उघडे", "common.explore": "पाहा →",
    "common.back_home": "← मुख्यपृष्ठावर परत",
    "cart.empty": "तुमची कार्ट रिकामी आहे.", "cart.title": "चौकशी कार्ट",
    "login.title": "सदस्य लॉगिन",
    "login.intro": "Google ने साइन इन करा. प्रथमच? तुम्हाला विद्यमान सदस्याकडून आमंत्रण कोड हवा.",
    "login.referral_label": "आमंत्रण कोड (परतीचे सदस्य असल्यास वैकल्पिक)",
    "login.cta": "Google सह सुरू ठेवा",
    "login.notice": "प्रत्येक सदस्यता ॲडमिन तपासतात. खोटी किंवा अपात्र आमंत्रणे आमंत्रणकर्त्यासह संबंधित सर्व खात्यांसह ब्लॉक केली जातील.",
    "register.title": "तुमचे आमंत्रण मागा",
    "register.subtitle": "sanskariBazarIn बंद आणि केवळ-सदस्य व्यासपीठ आहे. तुमच्या आमंत्रणकर्त्याचा कोड टाका.",
    "register.referral_label": "आमंत्रण कोड",
    "register.checking": "तपासत आहे…",
    "register.referred_by": "ने आमंत्रित केले",
    "register.invalid": "कोड ओळखला नाही",
    "register.accept": "मी या व्यासपीठावर अपशब्द वापरणार नाही, बेकायदेशीर पदार्थ विकणार नाही, तसेच कोणतेही प्रौढ छायाचित्र किंवा अश्लील साहित्य पोस्ट करणार नाही याची मी हमी देतो. मी खाली दिलेल्या अटी आणि अस्वीकरणही स्वीकारतो.",
    "register.policy_title": "समुदाय करार (आवश्यक)",
    "register.cta": "Google सह सुरू ठेवा",
    "register.review_notice": "नवीन खाती पूर्ण प्रवेशापूर्वी ॲडमिनद्वारे तपासली जातात.",
    "footer.platform": "व्यासपीठ", "footer.legal": "कायदेशीर",
    "footer.all_vendors": "सर्व विक्रेते", "footer.contact": "संपर्क",
    "footer.terms": "अटी व शर्ती", "footer.privacy": "गोपनीयता धोरण",
    "footer.disclaimer": "अस्वीकरण",
    "footer.about": "महाराष्ट्र आणि बाहेरील ब्राह्मण-मालकीच्या व्यवसायांचा शोध, संपर्क व संवाद यासाठी बंद, केवळ-सदस्य व्यासपीठ.",
    "footer.rights": "सर्व हक्क राखीव.",
    "footer.brand_subtitle": "सामुदायिक व्यवसाय शोध",

    /* ---------- Top ribbon ---------- */
    "ribbon.welcome": "sanskariBazarIn मध्ये आपले स्वागत आहे — निमंत्रण-आधारित समुदाय बाजार",
    "ribbon.cart": "कार्ट",

    /* ---------- VendorListing filter bar ---------- */
    "vl.eyebrow": "विक्रेते पहा",
    "vl.loading": "लोड होत आहे…",
    "vl.results_count": "{n} परिणाम — व्यवसाय, उत्पादने व सेवांचा शोध",
    "vl.title_default": "सर्व विक्रेते",
    "vl.title_city": "{city} मधील विक्रेते",
    "vl.title_category_city": "{city} मधील {category}",
    "vl.label_search": "शोधा",
    "vl.search_placeholder": "विक्रेता, वस्तू, शहर, श्रेणी शोधा — उदा. 'मोदक', '२ BHK', 'पैठणी साडी'",
    "vl.label_city": "शहर",
    "vl.all_cities": "सर्व शहरे",
    "vl.label_category": "श्रेणी",
    "vl.all_categories": "सर्व श्रेणी",
    "vl.label_sort": "क्रम",
    "vl.sort_recent": "अलीकडे जोडलेले",
    "vl.sort_rating": "रेटिंग",
    "vl.verified": "पडताळलेले",
    "vl.open_now": "आता उघडे",
    "vl.min_rating": "किमान रेटिंग",
    "vl.rating_any": "कोणतेही",
    "vl.view_grid": "ग्रिड",
    "vl.view_map": "नकाशा",
    "vl.per_page": "प्रति पृष्ठ",
    "vl.matched_items": "जुळणारी वस्तू",
    "vl.btn_view": "पहा",
    "vl.btn_whatsapp": "व्हॉट्सॲप",
    "vl.btn_chat": "संवाद",
    "vl.pagination": "पृष्ठ {page} / {total}",
    "vl.previous": "मागील",
    "vl.next": "पुढील",

    /* ---------- BecomeVendor wizard ---------- */
    "bv.eyebrow": "विक्रेता व्हा",
    "bv.title": "तुमचा व्यवसाय नोंदवा",
    "bv.subtitle": "तुमच्या व्यवसायाची माहिती भरा. प्रोफाइल इतर सदस्यांना दिसण्यापूर्वी ॲडमिनची मंजुरी आवश्यक आहे.",
    "bv.business_name": "व्यवसायाचे नाव *",
    "bv.category": "श्रेणी *",
    "bv.choose_category": "श्रेणी निवडा",
    "bv.city": "शहर *",
    "bv.choose_city": "शहर निवडा",
    "bv.description": "वर्णन",
    "bv.description_placeholder": "तुमच्या व्यवसायाबद्दल थोडक्यात",
    "bv.phone": "फोन",
    "bv.whatsapp": "व्हॉट्सॲप",
    "bv.address": "पत्ता",
    "bv.website": "वेबसाइट",
    "bv.hours": "कामाचे तास",
    "bv.service_area": "सेवा क्षेत्र",
    "bv.service_area_placeholder": "उदा. पुणे व आसपास",
    "bv.logo_url": "लोगो URL",
    "bv.banner_url": "बॅनर URL",
    "bv.location_title": "नकाशावर अचूक स्थान (पर्यायी)",
    "bv.location_text": "तुमच्या दुकानात उभे राहून खाली टॅप करा. {city} शहराच्या केंद्राऐवजी आम्ही अचूक पिन सेट करू.",
    "bv.recapture": "पुन्हा कॅप्चर",
    "bv.use_my_location": "माझे सध्याचे स्थान वापरा",
    "bv.location_captured": "स्थान कॅप्चर केले",
    "bv.geo_unsupported": "या डिव्हाइसवर जिओलोकेशन समर्थित नाही",
    "bv.geo_denied": "स्थान परवानगी नाकारली",
    "bv.geo_failed": "स्थान मिळवता आले नाही",
    "bv.disclaimer": "अस्वीकरण",
    "bv.cancel": "रद्द करा",
    "bv.submit": "मंजुरीसाठी पाठवा",
    "bv.saving": "सेव्ह होत आहे…",
    "bv.created_toast": "विक्रेता प्रोफाइल तयार झाले. ॲडमिन मंजुरीच्या प्रतीक्षेत.",
    "bv.create_failed": "विक्रेता तयार करता आला नाही",

    /* ---------- CartPage ---------- */
    "cp.eyebrow": "चौकशी कार्ट",
    "cp.items": "{n} वस्तू",
    "cp.item_singular": "{n} वस्तू",
    "cp.subtitle": "विक्रेत्यानुसार चौकशी गटात ठेवा. प्रत्येक विक्रेत्याला तुमची संपूर्ण यादी एका चॅट संदेशात मिळते — अटी, किंमत व वितरण परस्पर ठरवा.",
    "cp.clear_all": "सर्व साफ करा",
    "cp.empty": "तुमचे चौकशी कार्ट रिकामे आहे.",
    "cp.browse_vendors": "विक्रेते पहा",
    "cp.items_count": "{n} वस्तू",
    "cp.send_inquiry": "चौकशी पाठवा व संवाद सुरू करा",
    "cp.sent": "चौकशी विक्रेत्याला पाठवली",
    "cp.send_failed": "पाठवता आले नाही",

    /* ---------- NotificationCenter ---------- */
    "nc.eyebrow": "समुदायातील अद्यतने",
    "nc.title": "सूचना",
    "nc.empty": "अद्याप सूचना नाहीत.",
    "nc.from": "पासून",
    "nc.type_offer": "ऑफर",
    "nc.type_new_service": "नवीन सेवा",
    "nc.type_update": "अद्यतन",
    "nc.type_event": "इव्हेंट",

    /* ---------- VendorProfile ---------- */
    "vp.loading": "लोड होत आहे…",
    "vp.verified": "पडताळलेले",
    "vp.open_now": "आता उघडे",
    "vp.closed": "बंद",
    "vp.by": "द्वारा",
    "vp.chat_with_vendor": "विक्रेत्याशी संवाद",
    "vp.chat_on_whatsapp": "व्हॉट्सॲपवर संवाद",
    "vp.report": "तक्रार",
    "vp.report_dialog_title": "या विक्रेत्याबद्दल तक्रार करा",
    "vp.report_placeholder": "समस्येचे वर्णन करा…",
    "vp.report_submit": "पाठवा",
    "vp.report_cancel": "रद्द करा",
    "vp.report_success": "तक्रार ॲडमिनकडे पाठवली",
    "vp.offer": "ऑफर:",
    "vp.tab_products": "वस्तू / सेवा",
    "vp.tab_gallery": "गॅलरी",
    "vp.tab_reviews": "पुनरावलोकने",
    "vp.tab_contact": "संपर्क",
    "vp.tab_about": "आमच्याबद्दल",
    "vp.tab_map": "नकाशा",
    "vp.service_area": "सेवा क्षेत्र:",
    "vp.working_hours": "कामाचे तास:",
    "vp.no_products": "अद्याप वस्तू नोंदवल्या नाहीत.",
    "vp.videos": "व्हिडिओ",
    "vp.photos": "फोटो",
    "vp.no_gallery": "अद्याप गॅलरी आयटम नाहीत.",
    "vp.map_fallback": "{city} शहराचे केंद्रबिंदू दाखवत आहे. अचूक पिनसाठी विक्रेत्याला अचूक पत्ता शेअर करायला सांगा.",
    "vp.disclaimer": "अस्वीकरण",
    "vp.disclaimer_text": "व्यवहार, किंमत, वितरण, कर आणि सेवा अटी थेट ग्राहक व विक्रेत्यामध्ये असतात. sanskariBazarIn पेमेंट किंवा ऑर्डर सुलभ करत नाही.",
    "vp.add_to_cart_short": "चौकशी कार्टमध्ये जोडा",
    "vp.added_to_cart": "{name} चौकशी कार्टमध्ये जोडले",

    /* ---------- ChatPage ---------- */
    "chat.conversations": "संभाषणे",
    "chat.tab_active": "सक्रिय",
    "chat.tab_archived": "संग्रहित",
    "chat.empty_active": "अद्याप संभाषणे नाहीत.",
    "chat.empty_archived": "संग्रहित चॅट नाहीत.",
    "chat.archive": "संग्रहित करा",
    "chat.restore": "पुनर्संचयित करा",
    "chat.archived_toast": "चॅट संग्रहित केली",
    "chat.restored_toast": "चॅट पुनर्संचयित केली",
    "chat.update_failed": "चॅट अद्ययावत करता आली नाही",
    "chat.select_to_start": "संवाद सुरू करण्यासाठी एक संभाषण निवडा",
    "chat.browse_vendors": "किंवा विक्रेते पाहा →",
    "chat.subtitle": "थेट संवाद — व्यवहार व्यासपीठाबाहेर",
    "chat.view_profile": "प्रोफाइल पाहा",
    "chat.say_hello": "नमस्कार म्हणा…",
    "chat.be_first": "पहिले नमस्कार म्हणणारे व्हा.",
    "chat.image_ready": "प्रतिमा पाठवण्यास तयार",
    "chat.placeholder": "संदेश टाइप करा — किंवा प्रतिमा पेस्ट करा…",
    "chat.image_only": "केवळ प्रतिमा फाइल्स",
    "chat.upload_failed": "अपलोड अयशस्वी",
    "chat.vendor_fallback": "विक्रेता",
    "chat.read": "वाचले",
    "chat.sent": "पाठवले",

    /* ---------- VendorDashboard ---------- */
    "vd.title_eyebrow": "विक्रेता डॅशबोर्ड",
    "vd.approved": "मंजूर",
    "vd.awaiting_approval": "ॲडमिन मंजुरीच्या प्रतीक्षेत",
    "vd.verified": "पडताळलेले",
    "vd.unverified": "पडताळणी नाही",
    "vd.open_now_toggle": "आता उघडे",
    "vd.tab_info": "प्रोफाइल",
    "vd.tab_orders": "ऑर्डर / चौकशी",
    "vd.tab_notify": "सूचना",
    "vd.business_info": "व्यवसाय माहिती",
    "vd.business_name": "व्यवसायाचे नाव",
    "vd.category": "श्रेणी",
    "vd.city": "शहर",
    "vd.phone": "फोन",
    "vd.whatsapp": "व्हॉट्सॲप",
    "vd.website": "वेबसाइट",
    "vd.address": "पत्ता",
    "vd.description": "वर्णन",
    "vd.working_hours": "कामाचे तास",
    "vd.service_area": "सेवा क्षेत्र",
    "vd.map_pin": "नकाशा पिन",
    "vd.exact_pin": "अचूक पिन:",
    "vd.using_city_centroid": "सध्या {city} शहराचे केंद्रबिंदू वापरत आहोत. अचूक पिनसाठी तुमच्या दुकानाचे अचूक स्थान कॅप्चर करा.",
    "vd.recapture": "पुन्हा कॅप्चर",
    "vd.use_my_location": "माझे सध्याचे स्थान वापरा",
    "vd.location_captured": "स्थान कॅप्चर केले — सेव्ह करायला विसरू नका",
    "vd.location_cleared": "स्थान साफ केले",
    "vd.logo": "लोगो",
    "vd.banner": "बॅनर",
    "vd.offer_announcement": "ऑफर / घोषणा",
    "vd.offer_placeholder": "उत्सव १०% सूट…",
    "vd.gallery": "गॅलरी",
    "vd.gallery_count": "{n} / २५ प्रतिमा",
    "vd.embedded_videos": "एम्बेड केलेले व्हिडिओ (YouTube · Instagram · Facebook)",
    "vd.videos_count": "{n} / ५ व्हिडिओ",
    "vd.video_url_placeholder": "https://www.youtube.com/watch?v=… किंवा https://www.instagram.com/p/…",
    "vd.add_video_link": "+ व्हिडिओ लिंक जोडा",
    "vd.subscription_note": "बेस सदस्यता गॅलरीत २५ पर्यंत प्रतिमा आणि ५ पर्यंत एम्बेड व्हिडिओ अनुमत करते.",
    "vd.save": "सेव्ह करा",
    "vd.profile_saved": "प्रोफाइल सेव्ह केले",
    "vd.save_failed": "सेव्ह करता आले नाही",
    "vd.orders_heading": "ऑर्डर व चौकशी",
    "vd.orders_refresh": "रिफ्रेश",
    "vd.orders_note": "सेटलमेंट व पेमेंट तुमच्या व ग्राहकाच्या दरम्यान ऑफलाइन होते. तपशीलासाठी चॅट वापरा.",
    "vd.orders_empty": "अद्याप ऑर्डर नाहीत. ग्राहकांच्या कार्ट चौकशी प्रथम चॅटमध्ये येतात.",
    "vd.loading": "लोड होत आहे…",
    "vd.order_marked": "ऑर्डर {status} म्हणून चिन्हांकित केली",
    "vd.order_update_failed": "ऑर्डर अद्ययावत करता आली नाही",
    "vd.status_new": "नवीन",
    "vd.status_in_progress": "प्रगतीपथावर",
    "vd.status_delivered": "पाठवले",
    "vd.status_cancelled": "रद्द",
    "vd.notify_heading": "सूचना पाठवा",
    "vd.notify_subtitle": "समुदायातील सर्वांपर्यंत पोहोचा. (SMS / WhatsApp ब्रॉडकास्ट लवकरच.)",
    "vd.notify_title_placeholder": "शीर्षक (उदा. दिवाळी विशेष)",
    "vd.notify_type_offer": "नवीन ऑफर",
    "vd.notify_type_service": "नवीन सेवा",
    "vd.notify_type_update": "व्यवसाय अद्यतन",
    "vd.notify_type_event": "इव्हेंट घोषणा",
    "vd.notify_message": "संदेश",
    "vd.notify_send": "समुदायाला पाठवा",
    "vd.notify_sent": "सूचना पाठवली",
    "vd.notify_blocked": "सूचना पाठवण्यासाठी विक्रेता मंजूर असला पाहिजे",
    "vd.no_profile_title": "अद्याप विक्रेता प्रोफाइल नाही",
    "vd.no_profile_text": "चौकशी प्राप्त करण्यासाठी विक्रेता व्हा.",
    "vd.no_profile_cta": "विक्रेता व्हा",
    "vd.geo_unsupported": "जिओलोकेशन समर्थित नाही",
    "vd.geo_denied": "परवानगी नाकारली",
    "vd.geo_failed": "स्थान मिळवता आले नाही",
    "vd.clear_failed": "साफ करता आले नाही",
    "vd.products_label": "तुमच्या इन्व्हेंटरीतील वस्तू / सेवा",
    "vd.products_count": "{n} वस्तू",
    "vd.products_hint": "तुम्ही पुरवठा करत असलेल्या वस्तू, पदार्थ, सेवा किंवा मालमत्ता जोडा — प्रत्येकाला स्वतंत्र फोटो, वर्णन आणि सूचक किंमत देता येते. ग्राहक तुमच्या प्रोफाइलवर हे पाहतील.",
    "vd.product_add": "+ वस्तू जोडा",
    "vd.product_remove": "हटवा",
    "vd.product_name_placeholder": "वस्तूचे नाव (उदा. मोदक, २ BHK फ्लॅट, लग्न फोटोशूट)",
    "vd.product_price_placeholder": "सूचक किंमत (उदा. ₹१२०/बॉक्स, ₹६५ लाख)",
    "vd.product_desc_placeholder": "थोडक्यात वर्णन / स्पेक्स",

    /* ---------- Apps mega-menu ---------- */
    "apps.menu_label": "ॲप्स",
    "apps.menu_subtitle": "त्याच विश्वासू विक्रेता नेटवर्कवरील विशेष व्हर्टिकल्स",
    "apps.gharonda.name": "घरोंदा",
    "apps.gharonda.tagline": "घर शोधा — विक्री, भाडे, प्लॉट व बिल्डर्स",
    "apps.gharonda.cta": "मालमत्ता पहा",
    "apps.khavayye.name": "खवय्ये",
    "apps.khavayye.tagline": "घरगुती जेवण, टिफिन, केटरर्स, रेस्टॉरंट",
    "apps.khavayye.cta": "जेवण विक्रेते शोधा",
    "apps.soulmates.name": "सोल मेट्स",
    "apps.soulmates.tagline": "समुदायातील वधू-वर मध्यस्थ व लग्न सेवा",
    "apps.soulmates.cta": "मध्यस्थ शोधा",
    "apps.sabkuchfresh.name": "सब कुछ फ्रेश",
    "apps.sabkuchfresh.tagline": "ताजी फळे, भाजी, सेंद्रिय किराणा — थेट स्थानिक विक्रेत्यांकडून",
    "apps.sabkuchfresh.cta": "किराणा मागवा",
    "apps.niramaya.name": "निरामय",
    "apps.niramaya.tagline": "आयुर्वेद, अ‍ॅलोपॅथी, होमिओपॅथी व अधिक — आपल्या समाजातील आरोग्यसेवा",
    "apps.niramaya.cta": "वैद्यबंधू शोधा",
    "apps.rozgari.name": "रोजगारी",
    "apps.rozgari.tagline": "जीविका — समाजाच्या सदस्यांनी शेअर केलेल्या नोकऱ्या",
    "apps.rozgari.cta": "नोकऱ्या पहा",
    "apps.coming_soon": "लवकरच",
    "apps.view_all": "{name} ची सर्व सूची पहा",
    "apps.no_vendors": "येथे अद्याप पडताळलेले विक्रेते नाहीत. आपल्या समुदायातून एखाद्याला निमंत्रित करा →",
    "apps.filter_listing_type": "सूचीचा प्रकार",
    "apps.filter_bhk": "BHK",
    "apps.filter_cuisine": "खाद्य प्रकार",
    "apps.filter_service": "सेवेचा प्रकार",
    "apps.filter_delivery": "वितरण",
    "apps.filter_any": "कोणतेही",

    /* ---------- AdminDashboard ---------- */
    "ad.eyebrow": "ॲडमिन कन्सोल",
    "ad.title": "नियंत्रण कक्ष",
    "ad.subtitle": "सदस्य, विक्रेते, संदर्भ आणि अहवाल व्यवस्थापित करा.",
    "ad.stat_total_users": "एकूण वापरकर्ते",
    "ad.stat_approved_vendors": "मंजूर विक्रेते",
    "ad.stat_pending_vendors": "प्रलंबित विक्रेते",
    "ad.stat_pending_users": "प्रलंबित वापरकर्ते",
    "ad.stat_blocked": "ब्लॉक केलेले",
    "ad.stat_reports": "अहवाल",
    "ad.tab_users": "वापरकर्ते",
    "ad.tab_vendors": "विक्रेता मंजुरी",
    "ad.tab_analytics": "विश्लेषण",
    "ad.tab_referrals": "रेफरल वृक्ष",
    "ad.tab_reports": "अहवाल",
    "ad.tab_ads": "जाहिराती",
    "ad.tab_maintenance": "देखभाल",
    "ad.filter_all": "सर्व",
    "ad.filter_pending": "प्रलंबित",
    "ad.filter_approved": "मंजूर",
    "ad.filter_blocked": "ब्लॉक केलेले",
    "ad.search_placeholder": "शोधा…",
    "ad.search": "शोधा",
    "ad.col_name": "नाव",
    "ad.col_email": "ईमेल",
    "ad.col_role": "भूमिका",
    "ad.col_status": "स्थिती",
    "ad.col_referrer": "रेफरर कोड",
    "ad.col_actions": "क्रिया",
    "ad.col_business": "व्यवसाय",
    "ad.col_owner": "मालक",
    "ad.col_category": "श्रेणी",
    "ad.col_city": "शहर",
    "ad.col_vendor": "विक्रेता",
    "ad.col_reason": "कारण",
    "ad.col_when": "केव्हा",
    "ad.col_rating": "रेटिंग",
    "ad.btn_approve": "मंजूर करा",
    "ad.btn_block": "ब्लॉक",
    "ad.btn_block_cascade": "ब्लॉक + कॅस्केड",
    "ad.btn_unblock": "अनब्लॉक",
    "ad.btn_hide": "लपवा",
    "ad.user_approved": "वापरकर्ता मंजूर",
    "ad.user_blocked": "वापरकर्ता ब्लॉक केला",
    "ad.user_unblocked": "अनब्लॉक केले",
    "ad.cascade_blocked": "वापरकर्ता आणि अधीनस्थ ब्लॉक केले",
    "ad.vendor_approved": "विक्रेता मंजूर",
    "ad.vendor_hidden": "विक्रेता लपवला",
    "ad.analytics_by_city": "शहरानुसार विक्रेते",
    "ad.analytics_by_category": "श्रेणीनुसार विक्रेते",
    "ad.analytics_top": "उच्च रेटिंगचे विक्रेते",
    "ad.referrals_heading": "रेफरल वृक्ष",
    "ad.referrals_hint": "रेफरर आणि सर्व अधीनस्थ रेफरल ब्लॉक करण्यासाठी वापरकर्ते टॅबवर \"ब्लॉक + कॅस्केड\" वापरा.",
    "ad.reports_empty": "अहवाल नाहीत.",
    "ad.tag_approved": "मंजूर",
    "ad.tag_pending": "प्रलंबित",
    "ad.tag_blocked": "ब्लॉक",
  },
  hi: {
    "brand.tagline": "समुदाय खोज",
    "nav.cities": "शहर", "nav.categories": "श्रेणियाँ", "nav.vendors": "विक्रेता", "nav.apps": "ऐप्स",
    "header.login": "लॉगिन", "header.invite": "निमंत्रण माँगें", "header.cart": "कार्ट",
    "header.notifications": "सूचनाएँ", "header.messages": "संदेश",
    "menu.profile": "प्रोफ़ाइल", "menu.vendor_dashboard": "विक्रेता डैशबोर्ड",
    "menu.become_vendor": "विक्रेता बनें", "menu.admin": "एडमिन कंसोल", "menu.logout": "लॉगआउट",
    "hero.badge": "बंद समुदाय प्लेटफ़ॉर्म",
    "hero.title_1": "भरोसेमंद", "hero.title_brand": "ब्राह्मण-स्वामित्व वाले", "hero.title_2": "व्यवसाय खोजें — महाराष्ट्र और उससे आगे.",
    "hero.subtitle": "sanskariBazarIn विक्रेताओं, सेवाओं और हमारे समुदाय को एक साथ लाता है. कोई कार्ट नहीं, कोई भुगतान नहीं — सिर्फ़ सीधा खोज और उपभोक्ता एवं सत्यापित स्थानीय व्यवसायों के बीच बातचीत.",
    "hero.invite": "मेरे पास निमंत्रण कोड है", "hero.member_login": "सदस्य लॉगिन", "hero.explore": "शहर देखें",
    "hero.search_placeholder": "व्यवसाय, सेवा, उत्पाद, विक्रेता, शहर खोजें…",
    "stats.vendors": "विक्रेता", "stats.members": "सदस्य", "stats.cities": "शहर", "stats.categories": "श्रेणियाँ",
    "section.browse_city_eyebrow": "शहर के अनुसार ब्राउज़ करें",
    "section.browse_city_title": "महाराष्ट्र मूल में. भारत और विदेश भी.",
    "section.see_all_cities": "सभी {n} शहर देखें →",
    "section.nearest": "नज़दीकी शहर:",
    "section.bazaar_eyebrow": "इंडियन बाज़ार",
    "section.bazaar_title_1": "आज के", "section.bazaar_title_brand": "बाज़ार में",
    "section.bazaar_subtitle": "ट्रेंडिंग श्रेणियाँ, ताज़ी लिस्टिंग और सदस्य अभी क्या खोज रहे हैं. एक टैप में खोजें, बात करें और जुड़ें.",
    "section.bazaar_popular": "अभी लोकप्रिय आइटम",
    "whyus.referral_title": "केवल रेफ़रल से प्रवेश",
    "whyus.referral_text": "हर सदस्य आमंत्रित और सत्यापित होता है. समुदाय खुद को संवारता है.",
    "whyus.talk_title": "सीधे बात करें",
    "whyus.talk_text": "अंतर्निहित चैट आपको विक्रेताओं से जोड़ती है. कीमत, डिलीवरी और सेवा एक साथ तय करें.",
    "whyus.roots_title": "सांस्कृतिक जड़ें, आधुनिक उपकरण",
    "whyus.roots_text": "तीन हस्तनिर्मित थीम, 28 श्रेणियाँ और मराठी शैली डिज़ाइन — हमारे लोगों के लिए.",
    "common.search": "खोजें", "common.chat": "विक्रेता से चैट करें", "common.whatsapp": "व्हाट्सऐप",
    "common.add_to_cart": "पूछताछ कार्ट में जोड़ें", "common.send_inquiry": "पूछताछ भेजें",
    "common.featured": "चुनिंदा", "common.open_today": "आज खुला", "common.explore": "देखें →",
    "common.back_home": "← होम पर वापस",
    "cart.empty": "आपकी कार्ट खाली है.", "cart.title": "पूछताछ कार्ट",
    "login.title": "सदस्य लॉगिन",
    "login.intro": "Google से साइन इन करें. पहली बार? आपको मौजूदा सदस्य से रेफ़रल कोड चाहिए.",
    "login.referral_label": "रेफ़रल कोड (पुराने सदस्य के लिए वैकल्पिक)",
    "login.cta": "Google के साथ जारी रखें",
    "login.notice": "हर सदस्यता एडमिन द्वारा समीक्षित होती है. नकली या अपात्र रेफ़रल को रेफ़रर सहित संबंधित खातों के साथ ब्लॉक किया जाएगा.",
    "register.title": "अपना निमंत्रण माँगें",
    "register.subtitle": "sanskariBazarIn बंद और केवल-सदस्य है. अपने आमंत्रक का रेफ़रल कोड दर्ज करें.",
    "register.referral_label": "रेफ़रल कोड",
    "register.checking": "जाँच हो रही है…",
    "register.referred_by": "ने आमंत्रित किया",
    "register.invalid": "कोड पहचाना नहीं गया",
    "register.accept": "मैं इस प्लेटफ़ॉर्म पर अपशब्दों का उपयोग नहीं करूँगा, अवैध पदार्थ नहीं बेचूँगा, न ही किसी भी वयस्क छवि या अश्लील सामग्री को पोस्ट करूँगा. मैं नीचे दी गई शर्तें और अस्वीकरण भी स्वीकार करता हूँ.",
    "register.policy_title": "समुदाय अनुबंध (अनिवार्य)",
    "register.cta": "Google के साथ जारी रखें",
    "register.review_notice": "पूरी पहुँच मिलने से पहले नए खाते एडमिन द्वारा समीक्षित होते हैं.",
    "footer.platform": "प्लेटफ़ॉर्म", "footer.legal": "क़ानूनी",
    "footer.all_vendors": "सभी विक्रेता", "footer.contact": "संपर्क",
    "footer.terms": "शर्तें", "footer.privacy": "गोपनीयता नीति",
    "footer.disclaimer": "अस्वीकरण",
    "footer.about": "महाराष्ट्र और उससे आगे के ब्राह्मण-स्वामित्व वाले व्यवसायों को खोजने, जुड़ने और चैट करने के लिए बंद, केवल-सदस्य प्लेटफ़ॉर्म.",
    "footer.rights": "सर्वाधिकार सुरक्षित.",
    "footer.brand_subtitle": "सामुदायिक व्यवसाय खोज",

    /* ---------- Top ribbon ---------- */
    "ribbon.welcome": "sanskariBazarIn में आपका स्वागत है — आमंत्रण-आधारित समुदाय बाज़ार",
    "ribbon.cart": "कार्ट",

    /* ---------- VendorListing filter bar ---------- */
    "vl.eyebrow": "विक्रेता ब्राउज़ करें",
    "vl.loading": "लोड हो रहा है…",
    "vl.results_count": "{n} परिणाम — व्यवसाय, उत्पाद और सेवाओं की खोज",
    "vl.title_default": "सभी विक्रेता",
    "vl.title_city": "{city} में विक्रेता",
    "vl.title_category_city": "{city} में {category}",
    "vl.label_search": "खोजें",
    "vl.search_placeholder": "विक्रेता, वस्तु, शहर, श्रेणी खोजें — जैसे 'मोदक', '2 BHK', 'पैठणी साड़ी'",
    "vl.label_city": "शहर",
    "vl.all_cities": "सभी शहर",
    "vl.label_category": "श्रेणी",
    "vl.all_categories": "सभी श्रेणियाँ",
    "vl.label_sort": "क्रम",
    "vl.sort_recent": "हाल ही में जोड़े गए",
    "vl.sort_rating": "रेटिंग",
    "vl.verified": "सत्यापित",
    "vl.open_now": "अभी खुला",
    "vl.min_rating": "न्यूनतम रेटिंग",
    "vl.rating_any": "कोई भी",
    "vl.view_grid": "ग्रिड",
    "vl.view_map": "मानचित्र",
    "vl.per_page": "प्रति पृष्ठ",
    "vl.matched_items": "मिलान वाली वस्तुएँ",
    "vl.btn_view": "देखें",
    "vl.btn_whatsapp": "व्हाट्सऐप",
    "vl.btn_chat": "चैट",
    "vl.pagination": "पृष्ठ {page} / {total}",
    "vl.previous": "पिछला",
    "vl.next": "अगला",

    /* ---------- BecomeVendor wizard ---------- */
    "bv.eyebrow": "विक्रेता बनें",
    "bv.title": "अपना व्यवसाय सूचीबद्ध करें",
    "bv.subtitle": "अपने व्यवसाय की जानकारी भरें. प्रोफ़ाइल अन्य सदस्यों को दिखाई देने से पहले एडमिन स्वीकृति आवश्यक है.",
    "bv.business_name": "व्यवसाय का नाम *",
    "bv.category": "श्रेणी *",
    "bv.choose_category": "श्रेणी चुनें",
    "bv.city": "शहर *",
    "bv.choose_city": "शहर चुनें",
    "bv.description": "विवरण",
    "bv.description_placeholder": "अपने व्यवसाय के बारे में संक्षिप्त",
    "bv.phone": "फ़ोन",
    "bv.whatsapp": "व्हाट्सऐप",
    "bv.address": "पता",
    "bv.website": "वेबसाइट",
    "bv.hours": "कार्य समय",
    "bv.service_area": "सेवा क्षेत्र",
    "bv.service_area_placeholder": "जैसे पुणे और आसपास",
    "bv.logo_url": "लोगो URL",
    "bv.banner_url": "बैनर URL",
    "bv.location_title": "मानचित्र पर सटीक स्थान (वैकल्पिक)",
    "bv.location_text": "अपनी दुकान पर खड़े होकर नीचे टैप करें. {city} शहर के केंद्र के बजाय हम सटीक पिन लगाएंगे.",
    "bv.recapture": "पुनः कैप्चर",
    "bv.use_my_location": "मेरा वर्तमान स्थान उपयोग करें",
    "bv.location_captured": "स्थान कैप्चर किया",
    "bv.geo_unsupported": "इस डिवाइस पर जियोलोकेशन समर्थित नहीं",
    "bv.geo_denied": "स्थान अनुमति अस्वीकृत",
    "bv.geo_failed": "स्थान नहीं मिल सका",
    "bv.disclaimer": "अस्वीकरण",
    "bv.cancel": "रद्द करें",
    "bv.submit": "स्वीकृति के लिए भेजें",
    "bv.saving": "सेव हो रहा है…",
    "bv.created_toast": "विक्रेता प्रोफ़ाइल बना. एडमिन स्वीकृति की प्रतीक्षा में.",
    "bv.create_failed": "विक्रेता नहीं बना सका",

    /* ---------- CartPage ---------- */
    "cp.eyebrow": "पूछताछ कार्ट",
    "cp.items": "{n} वस्तुएँ",
    "cp.item_singular": "{n} वस्तु",
    "cp.subtitle": "विक्रेता के अनुसार पूछताछ समूह करें. प्रत्येक विक्रेता को आपकी पूरी सूची एक चैट संदेश में मिलती है — शर्तें, मूल्य और डिलीवरी आपस में तय करें.",
    "cp.clear_all": "सभी साफ़ करें",
    "cp.empty": "आपका पूछताछ कार्ट खाली है.",
    "cp.browse_vendors": "विक्रेता ब्राउज़ करें",
    "cp.items_count": "{n} वस्तुएँ",
    "cp.send_inquiry": "पूछताछ भेजें और चैट करें",
    "cp.sent": "पूछताछ विक्रेता को भेजी गई",
    "cp.send_failed": "भेज नहीं सका",

    /* ---------- NotificationCenter ---------- */
    "nc.eyebrow": "समुदाय से अपडेट",
    "nc.title": "सूचनाएँ",
    "nc.empty": "अभी कोई सूचना नहीं.",
    "nc.from": "की ओर से",
    "nc.type_offer": "ऑफ़र",
    "nc.type_new_service": "नई सेवा",
    "nc.type_update": "अपडेट",
    "nc.type_event": "इवेंट",

    /* ---------- VendorProfile ---------- */
    "vp.loading": "लोड हो रहा है…",
    "vp.verified": "सत्यापित",
    "vp.open_now": "अभी खुला",
    "vp.closed": "बंद",
    "vp.by": "द्वारा",
    "vp.chat_with_vendor": "विक्रेता से चैट",
    "vp.chat_on_whatsapp": "व्हाट्सऐप पर चैट",
    "vp.report": "रिपोर्ट",
    "vp.report_dialog_title": "इस विक्रेता की रिपोर्ट करें",
    "vp.report_placeholder": "समस्या का वर्णन करें…",
    "vp.report_submit": "भेजें",
    "vp.report_cancel": "रद्द करें",
    "vp.report_success": "रिपोर्ट एडमिन को भेजी गई",
    "vp.offer": "ऑफ़र:",
    "vp.tab_products": "उत्पाद / सेवाएँ",
    "vp.tab_gallery": "गैलरी",
    "vp.tab_reviews": "समीक्षाएँ",
    "vp.tab_contact": "संपर्क",
    "vp.tab_about": "हमारे बारे में",
    "vp.tab_map": "मानचित्र",
    "vp.service_area": "सेवा क्षेत्र:",
    "vp.working_hours": "कार्य समय:",
    "vp.no_products": "अभी कोई उत्पाद सूचीबद्ध नहीं.",
    "vp.videos": "वीडियो",
    "vp.photos": "तस्वीरें",
    "vp.no_gallery": "अभी गैलरी में कुछ नहीं.",
    "vp.map_fallback": "{city} शहर का केंद्र दिखाया जा रहा है. सटीक पिन के लिए विक्रेता से सटीक पता साझा करने को कहें.",
    "vp.disclaimer": "अस्वीकरण",
    "vp.disclaimer_text": "लेनदेन, मूल्य, डिलीवरी, कराधान और सेवा की शर्तें सीधे उपभोक्ता और विक्रेता के बीच होती हैं. sanskariBazarIn भुगतान या ऑर्डर की सुविधा नहीं देता.",
    "vp.add_to_cart_short": "पूछताछ कार्ट में जोड़ें",
    "vp.added_to_cart": "{name} पूछताछ कार्ट में जोड़ा गया",

    /* ---------- ChatPage ---------- */
    "chat.conversations": "बातचीत",
    "chat.tab_active": "सक्रिय",
    "chat.tab_archived": "संग्रहीत",
    "chat.empty_active": "अभी कोई बातचीत नहीं.",
    "chat.empty_archived": "कोई संग्रहीत चैट नहीं.",
    "chat.archive": "संग्रहीत करें",
    "chat.restore": "पुनर्स्थापित करें",
    "chat.archived_toast": "चैट संग्रहीत",
    "chat.restored_toast": "चैट पुनर्स्थापित",
    "chat.update_failed": "चैट अपडेट नहीं हो सकी",
    "chat.select_to_start": "बातचीत शुरू करने के लिए एक चयन करें",
    "chat.browse_vendors": "या विक्रेता देखें →",
    "chat.subtitle": "सीधी चैट — लेनदेन प्लेटफ़ॉर्म के बाहर",
    "chat.view_profile": "प्रोफ़ाइल देखें",
    "chat.say_hello": "नमस्ते कहें…",
    "chat.be_first": "नमस्ते कहने वाले पहले बनें.",
    "chat.image_ready": "छवि भेजने के लिए तैयार",
    "chat.placeholder": "संदेश लिखें — या छवि पेस्ट करें…",
    "chat.image_only": "केवल छवि फ़ाइलें",
    "chat.upload_failed": "अपलोड विफल",
    "chat.vendor_fallback": "विक्रेता",
    "chat.read": "पढ़ा",
    "chat.sent": "भेजा",

    /* ---------- VendorDashboard ---------- */
    "vd.title_eyebrow": "विक्रेता डैशबोर्ड",
    "vd.approved": "स्वीकृत",
    "vd.awaiting_approval": "एडमिन स्वीकृति की प्रतीक्षा में",
    "vd.verified": "सत्यापित",
    "vd.unverified": "असत्यापित",
    "vd.open_now_toggle": "अभी खुला",
    "vd.tab_info": "प्रोफ़ाइल",
    "vd.tab_orders": "ऑर्डर / पूछताछ",
    "vd.tab_notify": "सूचनाएँ",
    "vd.business_info": "व्यवसाय जानकारी",
    "vd.business_name": "व्यवसाय का नाम",
    "vd.category": "श्रेणी",
    "vd.city": "शहर",
    "vd.phone": "फ़ोन",
    "vd.whatsapp": "व्हाट्सऐप",
    "vd.website": "वेबसाइट",
    "vd.address": "पता",
    "vd.description": "विवरण",
    "vd.working_hours": "कार्य समय",
    "vd.service_area": "सेवा क्षेत्र",
    "vd.map_pin": "मानचित्र पिन",
    "vd.exact_pin": "सटीक पिन:",
    "vd.using_city_centroid": "अभी {city} शहर का केंद्र उपयोग में है. सटीक पिन के लिए अपनी दुकान का सटीक स्थान कैप्चर करें.",
    "vd.recapture": "पुनः कैप्चर",
    "vd.use_my_location": "मेरा वर्तमान स्थान उपयोग करें",
    "vd.location_captured": "स्थान कैप्चर किया — सेव करना याद रखें",
    "vd.location_cleared": "स्थान साफ़ किया",
    "vd.logo": "लोगो",
    "vd.banner": "बैनर",
    "vd.offer_announcement": "ऑफ़र / घोषणा",
    "vd.offer_placeholder": "त्योहारी 10% छूट…",
    "vd.gallery": "गैलरी",
    "vd.gallery_count": "{n} / 25 तस्वीरें",
    "vd.embedded_videos": "एम्बेडेड वीडियो (YouTube · Instagram · Facebook)",
    "vd.videos_count": "{n} / 5 वीडियो",
    "vd.video_url_placeholder": "https://www.youtube.com/watch?v=… या https://www.instagram.com/p/…",
    "vd.add_video_link": "+ वीडियो लिंक जोड़ें",
    "vd.subscription_note": "बेस सब्सक्रिप्शन गैलरी में 25 तस्वीरें और 5 एम्बेडेड वीडियो तक की अनुमति देता है.",
    "vd.save": "सेव करें",
    "vd.profile_saved": "प्रोफ़ाइल सेव हो गई",
    "vd.save_failed": "सेव नहीं हो सका",
    "vd.orders_heading": "ऑर्डर और पूछताछ",
    "vd.orders_refresh": "रिफ्रेश",
    "vd.orders_note": "सेटलमेंट और भुगतान आपके और ग्राहक के बीच ऑफ़लाइन होता है. विवरण के लिए चैट का उपयोग करें.",
    "vd.orders_empty": "अभी कोई ऑर्डर नहीं. ग्राहक की कार्ट पूछताछ पहले चैट में आती हैं.",
    "vd.loading": "लोड हो रहा है…",
    "vd.order_marked": "ऑर्डर {status} के रूप में चिह्नित",
    "vd.order_update_failed": "ऑर्डर अपडेट नहीं हो सका",
    "vd.status_new": "नया",
    "vd.status_in_progress": "प्रगति में",
    "vd.status_delivered": "वितरित",
    "vd.status_cancelled": "रद्द",
    "vd.notify_heading": "सूचना भेजें",
    "vd.notify_subtitle": "समुदाय के सभी लोगों तक पहुँचें. (SMS / WhatsApp ब्रॉडकास्ट — जल्द आ रहा है.)",
    "vd.notify_title_placeholder": "शीर्षक (जैसे दिवाली विशेष)",
    "vd.notify_type_offer": "नया ऑफ़र",
    "vd.notify_type_service": "नई सेवा",
    "vd.notify_type_update": "व्यवसाय अपडेट",
    "vd.notify_type_event": "इवेंट घोषणा",
    "vd.notify_message": "संदेश",
    "vd.notify_send": "समुदाय को भेजें",
    "vd.notify_sent": "सूचना भेजी गई",
    "vd.notify_blocked": "सूचना भेजने के लिए विक्रेता को स्वीकृत होना चाहिए",
    "vd.no_profile_title": "अभी कोई विक्रेता प्रोफ़ाइल नहीं",
    "vd.no_profile_text": "पूछताछ पाने के लिए विक्रेता बनें.",
    "vd.no_profile_cta": "विक्रेता बनें",
    "vd.geo_unsupported": "जियोलोकेशन समर्थित नहीं",
    "vd.geo_denied": "अनुमति अस्वीकृत",
    "vd.geo_failed": "स्थान नहीं मिल सका",
    "vd.clear_failed": "साफ़ नहीं हो सका",
    "vd.products_label": "आपकी इन्वेंटरी में उत्पाद / सेवाएँ",
    "vd.products_count": "{n} वस्तुएँ",
    "vd.products_hint": "जो वस्तुएँ, व्यंजन, सेवाएँ या संपत्ति आप प्रदान करते हैं उन्हें जोड़ें — प्रत्येक की अलग तस्वीर, विवरण और सांकेतिक मूल्य हो सकता है. ग्राहक आपके प्रोफ़ाइल पर ये देखेंगे.",
    "vd.product_add": "+ वस्तु जोड़ें",
    "vd.product_remove": "हटाएँ",
    "vd.product_name_placeholder": "वस्तु का नाम (जैसे मोदक, 2 BHK फ्लैट, शादी का फोटोशूट)",
    "vd.product_price_placeholder": "सांकेतिक मूल्य (जैसे ₹120/बॉक्स, ₹65 लाख)",
    "vd.product_desc_placeholder": "संक्षिप्त विवरण / विशेषताएँ",

    /* ---------- Apps mega-menu ---------- */
    "apps.menu_label": "ऐप्स",
    "apps.menu_subtitle": "उसी भरोसेमंद विक्रेता नेटवर्क पर विशेष वर्टिकल्स",
    "apps.gharonda.name": "घरोंदा",
    "apps.gharonda.tagline": "अपना घर खोजें — बिक्री, किराया, प्लॉट और बिल्डर्स",
    "apps.gharonda.cta": "संपत्ति देखें",
    "apps.khavayye.name": "खवय्ये",
    "apps.khavayye.tagline": "घरेलू भोजन, टिफिन, केटरर्स, रेस्तरां",
    "apps.khavayye.cta": "भोजन विक्रेता खोजें",
    "apps.soulmates.name": "सोल मेट्स",
    "apps.soulmates.tagline": "समुदाय के भीतर विवाह बिचौलिए और शादी सेवाएँ",
    "apps.soulmates.cta": "बिचौलिया खोजें",
    "apps.sabkuchfresh.name": "सब कुछ फ्रेश",
    "apps.sabkuchfresh.tagline": "ताज़े फल, सब्ज़ी, ऑर्गेनिक किराना — सीधे स्थानीय विक्रेताओं से",
    "apps.sabkuchfresh.cta": "किराना मंगवाएँ",
    "apps.niramaya.name": "निरामय",
    "apps.niramaya.tagline": "आयुर्वेद, एलोपैथी, होम्योपैथी और भी — अपने समुदाय की देखभाल",
    "apps.niramaya.cta": "चिकित्सक खोजें",
    "apps.rozgari.name": "रोज़गारी",
    "apps.rozgari.tagline": "जीविका — समुदाय के सदस्यों द्वारा साझा किए गए अवसर",
    "apps.rozgari.cta": "अवसर देखें",
    "apps.coming_soon": "जल्द आ रहा है",
    "apps.view_all": "{name} की सभी सूचियाँ देखें",
    "apps.no_vendors": "यहाँ अभी कोई सत्यापित विक्रेता नहीं. अपने समुदाय से किसी को आमंत्रित करें →",
    "apps.filter_listing_type": "सूची प्रकार",
    "apps.filter_bhk": "BHK",
    "apps.filter_cuisine": "व्यंजन प्रकार",
    "apps.filter_service": "सेवा प्रकार",
    "apps.filter_delivery": "डिलीवरी",
    "apps.filter_any": "कोई भी",

    /* ---------- AdminDashboard ---------- */
    "ad.eyebrow": "एडमिन कंसोल",
    "ad.title": "नियंत्रण कक्ष",
    "ad.subtitle": "सदस्य, विक्रेता, रेफ़रल और रिपोर्ट प्रबंधित करें.",
    "ad.stat_total_users": "कुल उपयोगकर्ता",
    "ad.stat_approved_vendors": "स्वीकृत विक्रेता",
    "ad.stat_pending_vendors": "लंबित विक्रेता",
    "ad.stat_pending_users": "लंबित उपयोगकर्ता",
    "ad.stat_blocked": "ब्लॉक किए",
    "ad.stat_reports": "रिपोर्ट",
    "ad.tab_users": "उपयोगकर्ता",
    "ad.tab_vendors": "विक्रेता स्वीकृति",
    "ad.tab_analytics": "विश्लेषण",
    "ad.tab_referrals": "रेफ़रल वृक्ष",
    "ad.tab_reports": "रिपोर्ट",
    "ad.tab_ads": "विज्ञापन",
    "ad.tab_maintenance": "रखरखाव",
    "ad.filter_all": "सभी",
    "ad.filter_pending": "लंबित",
    "ad.filter_approved": "स्वीकृत",
    "ad.filter_blocked": "ब्लॉक",
    "ad.search_placeholder": "खोजें…",
    "ad.search": "खोजें",
    "ad.col_name": "नाम",
    "ad.col_email": "ईमेल",
    "ad.col_role": "भूमिका",
    "ad.col_status": "स्थिति",
    "ad.col_referrer": "रेफ़रर कोड",
    "ad.col_actions": "क्रियाएँ",
    "ad.col_business": "व्यवसाय",
    "ad.col_owner": "मालिक",
    "ad.col_category": "श्रेणी",
    "ad.col_city": "शहर",
    "ad.col_vendor": "विक्रेता",
    "ad.col_reason": "कारण",
    "ad.col_when": "कब",
    "ad.col_rating": "रेटिंग",
    "ad.btn_approve": "स्वीकृत करें",
    "ad.btn_block": "ब्लॉक",
    "ad.btn_block_cascade": "ब्लॉक + कैस्केड",
    "ad.btn_unblock": "अनब्लॉक",
    "ad.btn_hide": "छिपाएँ",
    "ad.user_approved": "उपयोगकर्ता स्वीकृत",
    "ad.user_blocked": "उपयोगकर्ता ब्लॉक",
    "ad.user_unblocked": "अनब्लॉक किया",
    "ad.cascade_blocked": "उपयोगकर्ता और अधीनस्थ ब्लॉक",
    "ad.vendor_approved": "विक्रेता स्वीकृत",
    "ad.vendor_hidden": "विक्रेता छिपाया",
    "ad.analytics_by_city": "शहर के अनुसार विक्रेता",
    "ad.analytics_by_category": "श्रेणी के अनुसार विक्रेता",
    "ad.analytics_top": "शीर्ष रेटेड विक्रेता",
    "ad.referrals_heading": "रेफ़रल वृक्ष",
    "ad.referrals_hint": "रेफ़रर और सभी अधीनस्थ रेफ़रल ब्लॉक करने के लिए उपयोगकर्ता टैब पर \"ब्लॉक + कैस्केड\" का उपयोग करें.",
    "ad.reports_empty": "कोई रिपोर्ट नहीं.",
    "ad.tag_approved": "स्वीकृत",
    "ad.tag_pending": "लंबित",
    "ad.tag_blocked": "ब्लॉक",
  },
};

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem(KEY) || "en");
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  const setLang = useCallback((l) => { setLangState(l); localStorage.setItem(KEY, l); }, []);
  const t = useCallback((key, fallback = "") => {
    const cur = (DICT[lang] && DICT[lang][key]);
    if (cur !== undefined) return cur;
    const en = DICT.en[key];
    if (en !== undefined) return en;
    return fallback || key;
  }, [lang]);
  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
